import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Check, Repeat, Bell, Copy, Trash2, CheckCircle2, Sparkles, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Tip from "./Tip";
import RecurrenceEditor from "./RecurrenceEditor";
import DatePickerIT from "./ui/date-picker-it";
import { useModalA11y } from "../lib/useModalA11y";
import { useUnsavedChanges } from "../lib/useUnsavedChanges";
import { eisenhowerKeyFromTask, eisenhowerToPayload } from "../lib/eisenhower";
import { parseFieldErrors } from "../lib/formErrors";

const TYPES = [
  { v: "task", label: "Ciclo" },
  { v: "deadline", label: "Scadenza" },
  { v: "urgency", label: "Urgenza" },
  { v: "event", label: "Evento" },
  { v: "call", label: "Call" },
  { v: "meeting", label: "Meeting" },
  { v: "appointment", label: "Appuntamento" },
  { v: "payment_reminder", label: "Promemoria pagamento" },
  { v: "followup", label: "Follow-up" },
  { v: "habit", label: "Abitudine" },
  { v: "note", label: "Nota" },
];
const PRIORITIES = [
  { v: "critical", label: "Critica" }, { v: "high", label: "Alta" },
  { v: "medium", label: "Media" }, { v: "low", label: "Bassa" },
];
const RECUR_PRESETS = [
  { v: "", label: "Nessuna" },
  { v: "FREQ=DAILY", label: "Ogni giorno" },
  { v: "FREQ=WEEKLY", label: "Ogni settimana (stesso giorno)" },
  { v: "FREQ=WEEKLY;INTERVAL=2", label: "Ogni 2 settimane" },
  { v: "FREQ=MONTHLY", label: "Ogni mese (stesso giorno)" },
  { v: "FREQ=YEARLY", label: "Ogni anno (stessa data)" },
  { v: "__custom__", label: "Personalizzata…" },
];

const GOOGLE_DEFAULT_ON_TYPES = new Set(["event", "call", "meeting", "appointment"]);

const empty = {
  title: "", description: "", type: "task", priority: "medium",
  eisenhower: "", // Bug 17/50 · UI-only key: costruisci|gestisci|risolvi|nessuna|""
  // Bug 18/50 · Cicli reali — obiettivo del ciclo + override prossima azione.
  objective_text: "", next_action_hint: "",
  area_id: "", sub_area_id: "", project_id: "",
  due_date: "", due_time: "", scheduled_date: "", scheduled_time: "",
  recurrence_rule: "", recurrence_choice: "", recurrence_description: "",
  reminder_offsets_text: "",
  show_on_google_calendar: false, linked_calendar_id: "",
  meta_ids: [], // Fase 2b · mete a cui questo ciclo contribuisce
};

export default function TaskForm({ open, onClose, onSaved, initial = null, prefillScheduledDate = null, defaultType = null, prefillMetaId = null }) {
  const [form, setForm] = useState(empty);
  const [areas, setAreas] = useState([]);
  const [projects, setProjects] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [meteTree, setMeteTree] = useState({ annual: [], monthly: [], weekly: [] });
  const [googleConnected, setGoogleConnected] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [recurEditorOpen, setRecurEditorOpen] = useState(false);
  const dialogRef = useRef(null);

  useModalA11y(dialogRef, open, () => tryClose());
  useUnsavedChanges(dirty && open);

  useEffect(() => {
    if (!open) return;
    setDirty(false);
    api.listAreas().then(setAreas).catch(() => {});
    api.listProjects().then(setProjects).catch(() => {});
    // Fase 2b · load mete tree for the multi-select
    api.getMeteTree().then((t) => setMeteTree(t || { annual: [], monthly: [], weekly: [] })).catch(() => {});
    // Load Google integration state (best-effort; ignore errors when not configured)
    (async () => {
      try {
        const st = await api.googleStatus();
        setGoogleConnected(!!st?.connected);
        if (st?.connected) {
          const cals = await api.googleCalendars();
          setCalendars(Array.isArray(cals) ? cals : []);
        }
      } catch { setGoogleConnected(false); setCalendars([]); }
    })();
    if (initial) {
      setForm({
        title: initial.title || "", description: initial.description || "",
        type: initial.type || "task", priority: initial.priority || "medium",
        eisenhower: eisenhowerKeyFromTask(initial) || "",  // Bug 17/50
        objective_text: initial.objective_text || "",     // Bug 18/50
        next_action_hint: initial.next_action_hint || "", // Bug 18/50
        area_id: initial.area_id || "", sub_area_id: initial.sub_area_id || "",
        project_id: initial.project_id || "",
        due_date: initial.due_date || "",
        due_time: (initial.due_time || "").slice(0, 5),
        scheduled_date: initial.scheduled_date || "",
        scheduled_time: (initial.scheduled_time || "").slice(0, 5),
        recurrence_rule: initial.recurrence_rule || "",
        recurrence_choice: RECUR_PRESETS.some(p => p.v === (initial.recurrence_rule || "")) ? (initial.recurrence_rule || "") : (initial.recurrence_rule ? "__custom__" : ""),
        recurrence_description: initial.recurrence_description || "",
        reminder_offsets_text: (initial.reminder_offsets_days || []).join(","),
        show_on_google_calendar: !!initial.show_on_google_calendar,
        linked_calendar_id: initial.linked_calendar_id || "",
        meta_ids: [],
      });
      // Load currently-linked mete for this task
      api.getTaskMete(initial.id).then((rows) => {
        setForm((f) => ({ ...f, meta_ids: (rows || []).map((r) => r.id) }));
      }).catch(() => {});
    } else {
      const t = defaultType || empty.type;
      setForm({
        ...empty,
        type: t,
        scheduled_date: prefillScheduledDate || "",
        show_on_google_calendar: GOOGLE_DEFAULT_ON_TYPES.has(t),
        meta_ids: prefillMetaId ? [prefillMetaId] : [],
      });
    }
  }, [open, initial, prefillScheduledDate, defaultType, prefillMetaId]);

  const subAreas = form.area_id ? (areas.find((a) => a.id === form.area_id)?.sub_areas || []) : [];
  const primaryCalendarId = (calendars.find((c) => c.is_primary) || calendars[0])?.id || "";

  const update = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const changeType = (v) => update({ type: v, show_on_google_calendar: GOOGLE_DEFAULT_ON_TYPES.has(v) });

  const changeRecur = (choice) => {
    if (choice === "__custom__") {
      update({ recurrence_choice: choice, recurrence_rule: form.recurrence_rule || "", recurrence_description: form.recurrence_description || "" });
      setRecurEditorOpen(true);
    } else {
      update({ recurrence_choice: choice, recurrence_rule: choice, recurrence_description: "" });
    }
  };

  const handleRecurSave = ({ rrule, description }) => {
    update({ recurrence_choice: "__custom__", recurrence_rule: rrule, recurrence_description: description });
  };

  const tryClose = () => {
    if (dirty && !window.confirm("Modifiche non salvate. Vuoi davvero uscire?")) return;
    setDirty(false);
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const offsets = (form.reminder_offsets_text || "").split(",").map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n));
    const linkedCal = form.show_on_google_calendar
      ? (form.linked_calendar_id || primaryCalendarId || null)
      : null;
    const eisenPayload = eisenhowerToPayload(form.eisenhower);  // Bug 17/50
    const payload = {
      title: form.title, description: form.description || null,
      type: form.type, priority: form.priority,
      importance: eisenPayload.importance,
      urgency: eisenPayload.urgency,
      // Bug 18/50 · Cicli reali — invia obiettivo/next-hint solo per type=task.
      // Per gli altri type i campi non hanno senso semantico; li lasciamo null.
      objective_text: form.type === "task" ? (form.objective_text || null) : null,
      next_action_hint: form.type === "task" ? (form.next_action_hint || null) : null,
      area_id: form.area_id || null, sub_area_id: form.sub_area_id || null,
      project_id: form.project_id || null,
      due_date: form.due_date || null,
      due_time: form.due_time || null,
      scheduled_date: form.scheduled_date || null,
      scheduled_time: form.scheduled_time || null,
      recurrence_rule: form.recurrence_rule || null,
      recurrence_description: form.recurrence_description || null,
      reminder_offsets_days: offsets.length ? offsets : null,
      show_on_google_calendar: !!form.show_on_google_calendar,
      linked_calendar_id: linkedCal,
    };
    try {
      let taskId;
      if (initial) {
        await api.updateTask(initial.id, payload);
        taskId = initial.id;
      } else {
        const created = await api.createTask(payload);
        taskId = created?.id;
      }
      // Fase 2b · Sync meta links (diff-based)
      if (taskId) {
        try {
          const currentRows = initial ? await api.getTaskMete(taskId).catch(() => []) : [];
          const currentIds = new Set((currentRows || []).map((r) => r.id));
          const desiredIds = new Set(form.meta_ids || []);
          const toAdd = [...desiredIds].filter((id) => !currentIds.has(id));
          const toRemove = [...currentIds].filter((id) => !desiredIds.has(id));
          await Promise.all([
            ...toAdd.map((mid) => api.linkTaskToMeta(mid, taskId, 1.0)),
            ...toRemove.map((mid) => api.unlinkTaskFromMeta(mid, taskId)),
          ]);
        } catch { /* non-fatal */ }
      }
      // Sync meta links non deve mai bloccare la chiusura del modal:
      // se un link fallisce lo logghiamo e proseguiamo. Il toast di
      // successo + il refetch della lista devono comunque avvenire.
      toast.success(initial ? successUpdate : successCreate);
      setDirty(false);
      // onSaved può essere async o throwing (parent refetch API): non
      // deve mai impedire onClose(). Se lancia, mostriamo un toast
      // discreto ma chiudiamo comunque il modal — l'oggetto è già
      // salvato lato server.
      try { await Promise.resolve(onSaved?.()); }
      catch (e2) {
        console.warn("onSaved callback failed:", e2);
        toast.message("Salvato. Aggiorna la lista se non vedi la voce.");
      }
      onClose();
    } catch (e) {
      // Bug 26/50 · usa helper unificato che mappa i nomi campi in italiano
      // e differenzia 422 (array), custom HTTPException (stringa), network.
      const msg = parseFieldErrors(e, `Errore durante il salvataggio ${tl.singular}`);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const TYPE_LABELS = {
    task: { singular: "ciclo", article_new: "Nuovo", article_edit: "Modifica" },
    deadline: { singular: "scadenza", article_new: "Nuova", article_edit: "Modifica" },
    payment_reminder: { singular: "pagamento", article_new: "Nuovo", article_edit: "Modifica" },
    urgency: { singular: "urgenza", article_new: "Nuova", article_edit: "Modifica" },
    event: { singular: "evento", article_new: "Nuovo", article_edit: "Modifica" },
    habit: { singular: "abitudine", article_new: "Nuova", article_edit: "Modifica" },
    note: { singular: "nota", article_new: "Nuova", article_edit: "Modifica" },
    call: { singular: "call", article_new: "Nuova", article_edit: "Modifica" },
    meeting: { singular: "riunione", article_new: "Nuova", article_edit: "Modifica" },
    appointment: { singular: "appuntamento", article_new: "Nuovo", article_edit: "Modifica" },
    followup: { singular: "follow-up", article_new: "Nuovo", article_edit: "Modifica" },
  };
  const tl = TYPE_LABELS[form.type] || TYPE_LABELS.task;
  const titleText = initial ? `${tl.article_edit} ${tl.singular}` : `${tl.article_new} ${tl.singular}`;
  const submitTooltip = initial ? "Salva le modifiche" : `Crea ${tl.singular === "scadenza" || tl.singular === "urgenza" || tl.singular === "abitudine" || tl.singular === "nota" || tl.singular === "call" || tl.singular === "riunione" || tl.singular === "follow-up" ? "questa" : "questo"} ${tl.singular}`;
  const successCreate = tl.singular.charAt(0).toUpperCase() + tl.singular.slice(1) + (["scadenza","urgenza","abitudine","nota","call","riunione"].includes(tl.singular) ? " creata" : " creato");
  const successUpdate = tl.singular.charAt(0).toUpperCase() + tl.singular.slice(1) + (["scadenza","urgenza","abitudine","nota","call","riunione"].includes(tl.singular) ? " aggiornata" : " aggiornato");

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="task-form-overlay" onClick={tryClose}>
      <form onSubmit={submit}
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-form-title"
            className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="task-form">
        <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: "var(--pxos-border)" }}>
          <h3 id="task-form-title" className="font-display text-xl font-semibold" data-testid="task-form-title">{titleText}</h3>
          <Tip label="Chiudi senza salvare"><button type="button" onClick={tryClose} data-testid="task-form-close" aria-label="Chiudi"><X size={20}/></button></Tip>
        </div>
        <div className="p-5 space-y-4">
          <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Titolo</span>
            <input required value={form.title} onChange={(e) => update({ title: e.target.value })} className="pxos-input" data-testid="task-title" /></label>
          <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Descrizione</span>
            <textarea rows="2" value={form.description} onChange={(e) => update({ description: e.target.value })} className="pxos-input" data-testid="task-description" /></label>
          {/* Bug 18/50 · Cicli reali — campi visibili solo per type=task (i cicli). */}
          {form.type === "task" && (
            <>
              <label className="block" data-testid="task-objective-wrap">
                <span className="text-xs uppercase tracking-widest opacity-70">Obiettivo del ciclo (opzionale)</span>
                <textarea
                  rows="2" value={form.objective_text}
                  onChange={(e) => update({ objective_text: e.target.value })}
                  className="pxos-input"
                  placeholder="Es. Costruire l'ecosistema di clienti fidelizzati"
                  data-testid="task-objective-text"
                />
              </label>
              <label className="block" data-testid="task-next-action-hint-wrap">
                <span className="text-xs uppercase tracking-widest opacity-70">Prossima azione — override manuale (opzionale)</span>
                <input
                  type="text" value={form.next_action_hint}
                  onChange={(e) => update({ next_action_hint: e.target.value })}
                  className="pxos-input"
                  placeholder="Se vuoto: viene calcolata dalla prima azione non completata"
                  data-testid="task-next-action-hint"
                />
              </label>
            </>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Tipo</span>
              <select value={form.type} onChange={(e) => changeType(e.target.value)} className="pxos-input" data-testid="task-type">
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Priorità</span>
              <select value={form.priority} onChange={(e) => update({ priority: e.target.value })} className="pxos-input" data-testid="task-priority">
                {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
              </select></label>
            {/* Bug 17/50 · Matrice Eisenhower — solo 3 label visibili (+ Nessuna). */}
            <div className="sm:col-span-2">
              <div className="text-xs uppercase tracking-widest opacity-70 mb-1.5">Quadrante</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" role="radiogroup" aria-label="Quadrante Eisenhower" data-testid="task-eisenhower-group">
                {[
                  { v: "costruisci", label: "Costruisci", hint: "importante · può aspettare", cls: "border-emerald-300 bg-emerald-50" },
                  { v: "gestisci",   label: "Gestisci",   hint: "importante e urgente",       cls: "border-red-300 bg-red-50" },
                  { v: "risolvi",    label: "Risolvi",    hint: "non importante ma urgente",  cls: "border-amber-300 bg-amber-50" },
                  { v: "nessuna",    label: "Nessuna",    hint: "senza urgenza né priorità",  cls: "border-slate-200 bg-slate-50" },
                ].map((q) => {
                  const sel = form.eisenhower === q.v;
                  return (
                    <label key={q.v}
                           className={`cursor-pointer rounded-xl border-2 p-3 transition ${sel ? q.cls + " ring-2 ring-offset-1" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                           data-testid={`task-eisenhower-${q.v}`}>
                      <input type="radio" name="eisenhower" value={q.v}
                             checked={sel} onChange={() => update({ eisenhower: q.v })}
                             className="sr-only" />
                      <div className="text-sm font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{q.label}</div>
                      <div className="text-[11px] opacity-70 mt-0.5">{q.hint}</div>
                    </label>
                  );
                })}
              </div>
            </div>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Area</span>
              <select value={form.area_id} onChange={(e) => update({ area_id: e.target.value, sub_area_id: "" })} className="pxos-input" data-testid="task-area">
                <option value="">—</option>
                {areas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Sotto-area</span>
              <select value={form.sub_area_id} onChange={(e) => update({ sub_area_id: e.target.value })} className="pxos-input" disabled={!form.area_id} data-testid="task-sub-area">
                <option value="">—</option>
                {subAreas.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Progetto</span>
              <select value={form.project_id} onChange={(e) => update({ project_id: e.target.value })} className="pxos-input" data-testid="task-project">
                <option value="">—</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Scadenza (due_date)</span>
              <div className="grid grid-cols-[1fr_100px] gap-2">
                <DatePickerIT value={form.due_date} onChange={(v) => update({ due_date: v })} testId="task-due-date" placeholder="gg/mm/aaaa" />
                <input type="time" value={form.due_time} onChange={(e) => update({ due_time: e.target.value })} className="pxos-input" data-testid="task-due-time" disabled={!form.due_date} placeholder="—" />
              </div>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-widest opacity-70">Programmata (scheduled_date)</span>
              <div className="grid grid-cols-[1fr_100px] gap-2">
                <DatePickerIT value={form.scheduled_date} onChange={(v) => update({ scheduled_date: v })} testId="task-scheduled-date" placeholder="gg/mm/aaaa" />
                <input type="time" value={form.scheduled_time} onChange={(e) => update({ scheduled_time: e.target.value })} className="pxos-input" data-testid="task-scheduled-time" disabled={!form.scheduled_date} placeholder="—" />
              </div>
              <div className="text-[10px] opacity-60 mt-1">Suggerimento: puoi trascinare il ciclo nell'agenda per riprogrammarlo (aggiorna solo la data programmata, mai la scadenza).</div>
            </label>
          </div>

          {/* Google Calendar integration */}
          <div className="pxos-card p-3 space-y-2 bg-white/60" data-testid="task-google-block">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1"><Link2 size={13}/> Google Calendar</div>
            {googleConnected ? (
              <>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!form.show_on_google_calendar}
                          onChange={(e) => update({ show_on_google_calendar: e.target.checked })}
                          data-testid="task-google-toggle" />
                  <span>Mostra su Google Calendar</span>
                </label>
                {form.show_on_google_calendar && (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-widest opacity-70">Calendario di destinazione</span>
                    <select
                      value={form.linked_calendar_id || primaryCalendarId}
                      onChange={(e) => update({ linked_calendar_id: e.target.value })}
                      className="pxos-input"
                      data-testid="task-google-calendar-select"
                    >
                      {calendars.length === 0 && <option value="">Nessun calendario disponibile</option>}
                      {calendars.map((c) => (
                        <option key={c.id} value={c.id}>{c.summary || c.name || c.id}{c.is_primary ? " (principale)" : ""}</option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="text-[10px] opacity-60">Predefinito: attivo per Evento / Call / Meeting / Appuntamento.</div>
              </>
            ) : (
              <div className="text-xs opacity-80">
                Google Calendar non collegato.{" "}
                <Link to="/settings/integrations/google" className="underline font-medium" data-testid="task-google-connect-cta">
                  Collega Google Calendar
                </Link>
                {" "}per pubblicare eventi e call.
              </div>
            )}
          </div>

          <div className="pxos-card p-3 space-y-2 bg-white/60">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1"><Repeat size={13}/> Si ripete?</div>
            <select value={form.recurrence_choice} onChange={(e) => changeRecur(e.target.value)} className="pxos-input" data-testid="task-recurrence-choice">
              {RECUR_PRESETS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
            </select>
            {form.recurrence_choice === "__custom__" && (
              <div className="space-y-2">
                {form.recurrence_rule && form.recurrence_description ? (
                  <div className="rounded-lg border p-2 text-xs flex items-start gap-2" style={{ borderColor: "#B6E0C4", background: "#F0FBF4" }} data-testid="task-recurrence-summary">
                    <Sparkles size={12} style={{ color: "#1F7A2C" }} className="mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <div className="font-medium" style={{ color: "var(--pxos-text-dark)" }}>{form.recurrence_description}</div>
                      <div className="text-[10px] font-mono opacity-60" style={{ color: "var(--pxos-text-dark)" }}>{form.recurrence_rule}</div>
                    </div>
                    <button type="button" onClick={() => setRecurEditorOpen(true)}
                            className="text-[10px] font-semibold underline"
                            style={{ color: "#1F7A2C" }}
                            data-testid="task-recurrence-edit">
                      Modifica
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input value={form.recurrence_rule} onChange={(e) => update({ recurrence_rule: e.target.value })}
                           placeholder="es. FREQ=MONTHLY;BYMONTHDAY=15" className="pxos-input font-mono text-xs flex-1" data-testid="task-recurrence-custom" />
                    <Tip label="Descrivi la ricorrenza in italiano">
                      <button type="button" onClick={() => setRecurEditorOpen(true)}
                              className="rounded-full border px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-black/5"
                              style={{ borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}
                              data-testid="task-recurrence-open-nl">
                        <Sparkles size={12} /> A parole
                      </button>
                    </Tip>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="pxos-card p-3 space-y-2 bg-white/60">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1"><Bell size={13}/> Reminder (giorni prima, separati da virgola)</div>
            <input placeholder="30,15,3,0" value={form.reminder_offsets_text} onChange={(e) => update({ reminder_offsets_text: e.target.value })}
                   className="pxos-input font-mono" data-testid="task-reminder-offsets" />
            <div className="text-xs opacity-60">Vuoto = default: 30/15/3/0 per scadenze, 1/0 per cicli.</div>
          </div>

          {/* Fase 2b · Contribuisce alle azioni */}
          <div className="pxos-card p-3 space-y-2 bg-white/60" data-testid="task-azioni-block">
            <div className="text-xs uppercase tracking-widest opacity-70 flex items-center gap-1">
              <Link2 size={13}/> Contribuisce all'azione
            </div>
            {(meteTree.annual.length + meteTree.monthly.length + meteTree.weekly.length) === 0 ? (
              <div className="text-xs opacity-70">
                Nessuna azione ancora creata. <Link to="/azioni" className="underline font-medium">Vai al modulo Azioni</Link> per iniziare.
              </div>
            ) : (
              <>
                {["annual", "monthly", "weekly"].map((lvl) => {
                  const items = (meteTree[lvl] || []).filter((m) => m.status === "active");
                  if (items.length === 0) return null;
                  const label = lvl === "annual" ? "Anno" : lvl === "monthly" ? "Mese" : "Settimana";
                  return (
                    <div key={lvl} className="space-y-1">
                      <div className="text-[10px] uppercase tracking-widest opacity-60">{label}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((m) => {
                          const selected = form.meta_ids.includes(m.id);
                          return (
                            <button type="button" key={m.id}
                                    onClick={() => update({
                                      meta_ids: selected
                                        ? form.meta_ids.filter((id) => id !== m.id)
                                        : [...form.meta_ids, m.id],
                                    })}
                                    className={`text-xs px-2.5 py-1 rounded-full border transition ${selected ? "bg-black/85 text-white border-black" : "bg-white hover:bg-black/[0.03]"}`}
                                    style={selected ? {} : { borderColor: "var(--pxos-border)", color: "var(--pxos-text-dark)" }}
                                    data-testid={`task-azione-chip-${m.id}`}>
                              {selected && "✓ "}{m.title}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                <div className="text-[10px] opacity-60 mt-1">
                  Al completamento del ciclo, il progresso delle azioni collegate si aggiorna automaticamente.
                </div>
              </>
            )}
          </div>
        </div>
        <div className="p-5 border-t flex gap-2 flex-wrap items-center" style={{ borderColor: "var(--pxos-border)" }}>
          <Tip label={submitTooltip}>
            <button
              type="submit"
              disabled={busy}
              className="pxos-btn-primary inline-flex items-center gap-1.5"
              data-testid="task-submit"
              aria-busy={busy}
            >
              {busy ? (
                <>
                  <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" aria-hidden="true" />
                  Salvataggio…
                </>
              ) : (
                <>
                  <Check size={14}/> {initial ? "Aggiorna" : "Crea"}
                </>
              )}
            </button>
          </Tip>
          <button type="button" disabled={busy} className="text-sm underline opacity-70 disabled:opacity-30" onClick={tryClose} data-testid="task-cancel" title="Annulla senza salvare">Annulla</button>
          {initial && (
            <div className="ms-auto flex items-center gap-1">
              <Tip label="Segna come completato">
                <button type="button"
                        onClick={async () => {
                          try { await api.completeTask(initial.id); toast.success("✓ Completato"); setDirty(false); onSaved?.(); onClose(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                        }}
                        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs hover:bg-green-50"
                        data-testid="task-quick-complete">
                  <CheckCircle2 size={14} className="text-green-700"/> Completa
                </button>
              </Tip>
              <Tip label="Crea una copia di questo ciclo">
                <button type="button"
                        onClick={async () => {
                          try { await api.duplicateTask(initial.id); toast.success("Duplicato"); setDirty(false); onSaved?.(); onClose(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                        }}
                        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs hover:bg-black/[0.03]"
                        data-testid="task-quick-duplicate">
                  <Copy size={14}/> Duplica
                </button>
              </Tip>
              <Tip label="Sposta nel cestino (recuperabile per 30 giorni)">
                <button type="button"
                        onClick={async () => {
                          if (!confirm("Spostare questo ciclo nel cestino?")) return;
                          try { await api.trashTask(initial.id); toast.success("Spostato nel cestino"); setDirty(false); onSaved?.(); onClose(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                        }}
                        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs text-red-700 hover:bg-red-50"
                        data-testid="task-quick-delete">
                  <Trash2 size={14}/> Elimina
                </button>
              </Tip>
            </div>
          )}
        </div>
      </form>
      <RecurrenceEditor
        open={recurEditorOpen}
        onClose={() => setRecurEditorOpen(false)}
        onSave={handleRecurSave}
        baseDate={form.due_date || form.scheduled_date || new Date().toISOString().slice(0, 10)}
        initialRule={form.recurrence_rule}
        initialDescription={form.recurrence_description}
      />
    </div>
  );
}
